/* =====================================================================
 * useDMWebSocket — DM WebSocket 연결/재연결 관리 훅
 *
 * 고도화 기능:
 *   - Jitter 기반 exponential backoff 재연결
 *   - 최대 재연결 횟수 제한 (30회)
 *   - Flapping 방지 (30초 이상 연결 유지 시에만 backoff 리셋)
 *   - navigator.onLine 연동
 *   - Double-probe 연결 검증
 *   - In-band 토큰 갱신 (token_expiring_soon → token_refresh)
 *   - 클라이언트 ACK 프로토콜 + seq 중복 제거
 *   - 3단계 연결 상태 (connected / reconnecting / disconnected)
 * ===================================================================== */

import { useCallback, useEffect, useRef } from 'react';
import { getDMWebSocketUrl } from '@auw/api-client';
import { ensureFreshToken } from '@auw/api-client';
import { connectionHealth } from '@auw/api-client';
import { useDMStore } from './useDMStore';
import { useDMConversationStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import type { DMAgentType, DMMessage, UserMessage } from '@auw/workspace-utils/types/dm';
import {
  loadPersistedOutboxEntries,
  prunePersistedOutbox,
  removePersistedOutboxEntry,
  upsertPersistedOutboxEntry,
} from './dmOutboxPersistence';

const MAX_RECONNECT_DELAY = 30_000;
const INITIAL_RECONNECT_DELAY = 1_000;
const PING_INTERVAL = 25_000;
const PONG_TIMEOUT = 10_000;
const PROBE_PONG_TIMEOUT = 5_000;
const MAX_RECONNECT_ATTEMPTS = 30;
const STABLE_CONNECTION_MS = 30_000;
const PROCESSED_SEQ_LIMIT = 1000;
const MAX_PENDING_BY_SEQ = 2000;
const AUTH_TIMEOUT_MS = 10_000;
const OUTBOX_RETRY_BASE_MS = 1_000;
const OUTBOX_RETRY_MAX_MS = 30_000;
const OUTBOX_MAX_PENDING = 300;
const OUTBOX_MAX_RETRIES = 20;
const ACK_BATCH_FLUSH_MS = 100;
const ACK_BATCH_MAX_SIZE = 64;

type InboundEvent = {
  type?: string;
  seq?: number;
  data?: Record<string, unknown>;
  [k: string]: unknown;
};

type OutboxEntry = {
  payload: string;
  retries: number;
  lastSentAt: number;
  retryDelayMs: number;
  trackSentMetric: boolean;
};

export function useDMWebSocket(enabled = true) {
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const reconnectDelay = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectAttempts = useRef(0);
  const isUnmounting = useRef(false);
  const pingTimer = useRef<ReturnType<typeof setInterval>>();
  const pongTimer = useRef<ReturnType<typeof setTimeout>>();
  const lastSeq = useRef(0);
  const lastAppliedSeq = useRef(0);
  const connectedAt = useRef(0);
  const processedSeqs = useRef(new Set<number>());
  const pendingBySeq = useRef(new Map<number, InboundEvent>());
  const outbox = useRef(new Map<string, OutboxEntry>());
  const outboxTimer = useRef<ReturnType<typeof setInterval>>();
  const lastResumeRequestAt = useRef(0);
  const lastPingAt = useRef(0);
  const authToken = useRef('');
  const isAuthenticated = useRef(false);
  const authTimer = useRef<ReturnType<typeof setTimeout>>();
  const preAuthQueue = useRef<InboundEvent[]>([]);
  const outboxHydrated = useRef(false);
  const ackQueue = useRef<Set<number>>(new Set());
  const ackFlushTimer = useRef<ReturnType<typeof setTimeout>>();

  const {
    addMessage,
    setTyping,
    clearTyping,
    setWsConnected,
  } = useDMStore();

  const persistOutboxEntry = useCallback((id: string, entry: OutboxEntry) => {
    void upsertPersistedOutboxEntry({
      id,
      payload: entry.payload,
      retries: entry.retries,
      lastSentAt: entry.lastSentAt,
      retryDelayMs: entry.retryDelayMs,
      trackSentMetric: entry.trackSentMetric,
      updatedAt: Date.now(),
    });
  }, []);

  const removeOutboxPersistence = useCallback((id: string) => {
    void removePersistedOutboxEntry(id);
  }, []);

  const hydrateOutbox = useCallback(async () => {
    if (outboxHydrated.current) return;
    const rows = await loadPersistedOutboxEntries(OUTBOX_MAX_PENDING);
    if (rows.length === 0) {
      outboxHydrated.current = true;
      return;
    }
    for (const row of rows) {
      if (!row?.id || typeof row.payload !== 'string') continue;
      outbox.current.set(row.id, {
        payload: row.payload,
        retries: Number.isFinite(row.retries) ? row.retries : 0,
        lastSentAt: Number.isFinite(row.lastSentAt) ? row.lastSentAt : 0,
        retryDelayMs: Number.isFinite(row.retryDelayMs)
          ? Number(row.retryDelayMs)
          : OUTBOX_RETRY_BASE_MS,
        trackSentMetric: Boolean(row.trackSentMetric),
      });
    }
    outboxHydrated.current = true;
  }, []);

  // ---- seq 중복 제거 ----
  const isNewSeq = useCallback((seq: number): boolean => {
    if (processedSeqs.current.has(seq)) return false;
    processedSeqs.current.add(seq);
    if (processedSeqs.current.size > PROCESSED_SEQ_LIMIT) {
      const arr = [...processedSeqs.current];
      processedSeqs.current = new Set(arr.slice(-PROCESSED_SEQ_LIMIT / 2));
    }
    return true;
  }, []);

  const nextOutboxDelay = useCallback((retries: number): number => {
    const expo = Math.min(OUTBOX_RETRY_MAX_MS, OUTBOX_RETRY_BASE_MS * (2 ** Math.max(0, retries - 1)));
    const jitter = 0.8 + Math.random() * 0.4;
    return Math.max(OUTBOX_RETRY_BASE_MS, Math.floor(expo * jitter));
  }, []);

  // ---- ACK 전송 ----
  const flushAckQueue = useCallback(() => {
    if (ackFlushTimer.current) {
      clearTimeout(ackFlushTimer.current);
      ackFlushTimer.current = undefined;
    }
    if (!isAuthenticated.current || ws.current?.readyState !== WebSocket.OPEN) return;
    if (ackQueue.current.size === 0) return;

    const seqs = Array.from(ackQueue.current)
      .sort((a, b) => a - b)
      .slice(0, ACK_BATCH_MAX_SIZE);
    if (seqs.length === 0) return;
    for (const seq of seqs) {
      ackQueue.current.delete(seq);
    }
    ws.current.send(JSON.stringify({ type: 'ack', data: { seqs } }));
    if (ackQueue.current.size > 0) {
      ackFlushTimer.current = setTimeout(flushAckQueue, ACK_BATCH_FLUSH_MS);
    }
  }, []);

  const scheduleAckFlush = useCallback(() => {
    if (ackFlushTimer.current) return;
    ackFlushTimer.current = setTimeout(flushAckQueue, ACK_BATCH_FLUSH_MS);
  }, [flushAckQueue]);

  const sendAck = useCallback((seq: number) => {
    if (!isAuthenticated.current || ws.current?.readyState !== WebSocket.OPEN) return;
    ackQueue.current.add(seq);
    scheduleAckFlush();
  }, [scheduleAckFlush]);

  const makeClientMessageId = useCallback(
    () => `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`,
    [],
  );

  const makeTraceId = useCallback(
    () => `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`,
    [],
  );

  const sendOutboxEntry = useCallback((clientMsgId: string) => {
    const entry = outbox.current.get(clientMsgId);
    if (!entry) return;
    if (!isAuthenticated.current || ws.current?.readyState !== WebSocket.OPEN) return;
    if (entry.retries >= OUTBOX_MAX_RETRIES) return;

    ws.current.send(entry.payload);
    entry.retries += 1;
    entry.lastSentAt = Date.now();
    entry.retryDelayMs = nextOutboxDelay(entry.retries);
    persistOutboxEntry(clientMsgId, entry);
    if (entry.trackSentMetric) {
      connectionHealth.recordMessageSent('dm');
    }
  }, [nextOutboxDelay, persistOutboxEntry]);

  const enqueueOutbound = useCallback((
    type: string,
    data: Record<string, unknown>,
    trackSentMetric = false,
  ) => {
    const clientMsgId = makeClientMessageId();
    if (outbox.current.size >= OUTBOX_MAX_PENDING) {
      const oldest = outbox.current.keys().next().value as string | undefined;
      if (oldest) {
        outbox.current.delete(oldest);
        removeOutboxPersistence(oldest);
      }
    }

    const payload = JSON.stringify({
      type,
      client_msg_id: clientMsgId,
      data: {
        ...data,
        trace_id: makeTraceId(),
      },
    });

    const entry: OutboxEntry = {
      payload,
      retries: 0,
      lastSentAt: 0,
      retryDelayMs: OUTBOX_RETRY_BASE_MS,
      trackSentMetric,
    };
    outbox.current.set(clientMsgId, entry);
    persistOutboxEntry(clientMsgId, entry);
    void prunePersistedOutbox(OUTBOX_MAX_PENDING);

    sendOutboxEntry(clientMsgId);
  }, [
    makeClientMessageId,
    makeTraceId,
    persistOutboxEntry,
    removeOutboxPersistence,
    sendOutboxEntry,
  ]);

  const flushOutbox = useCallback((force = false) => {
    if (!isAuthenticated.current || ws.current?.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    for (const [id, entry] of outbox.current.entries()) {
      if (entry.retries >= OUTBOX_MAX_RETRIES) {
        outbox.current.delete(id);
        removeOutboxPersistence(id);
        continue;
      }
      if (force || now - entry.lastSentAt >= entry.retryDelayMs) {
        sendOutboxEntry(id);
      }
    }
  }, [removeOutboxPersistence, sendOutboxEntry]);

  // ---- Heartbeat ----
  const stopHeartbeat = useCallback(() => {
    if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = undefined; }
    if (pongTimer.current) { clearTimeout(pongTimer.current); pongTimer.current = undefined; }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    pingTimer.current = setInterval(() => {
      if (isAuthenticated.current && ws.current?.readyState === WebSocket.OPEN) {
        lastPingAt.current = Date.now();
        ws.current.send(JSON.stringify({ type: 'ping' }));
        pongTimer.current = setTimeout(() => {
          // Double-probe: 한번 더 시도 후 최종 판단
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
            pongTimer.current = setTimeout(() => {
              ws.current?.close();
            }, PROBE_PONG_TIMEOUT);
          } else {
            ws.current?.close();
          }
        }, PONG_TIMEOUT);
      }
    }, PING_INTERVAL);
  }, [stopHeartbeat]);

  const handlePong = useCallback(() => {
    if (pongTimer.current) { clearTimeout(pongTimer.current); pongTimer.current = undefined; }
    connectionHealth.recordPong('dm', lastPingAt.current);
  }, []);

  const finishAuthHandshake = useCallback((serverCurrentSeq?: number) => {
    if (authTimer.current) {
      clearTimeout(authTimer.current);
      authTimer.current = undefined;
    }
    isAuthenticated.current = true;
    connectedAt.current = Date.now();
    reconnectAttempts.current = 0;
    reconnectDelay.current = INITIAL_RECONNECT_DELAY;
    setWsConnected(true);
    connectionHealth.markConnected('dm');
    startHeartbeat();

    if (typeof serverCurrentSeq === 'number' && serverCurrentSeq > 0 && lastAppliedSeq.current === 0) {
      lastAppliedSeq.current = serverCurrentSeq;
      lastSeq.current = Math.max(lastSeq.current, serverCurrentSeq);
    } else if (lastSeq.current > 0 && ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'resume',
        data: { last_seq: lastSeq.current },
      }));
    }

    useDMStore.getState().loadUnreadCounts();
    useDMConversationStore.getState().loadConversations();
    flushOutbox(true);
  }, [flushOutbox, setWsConnected, startHeartbeat]);

  const processServerEvent = useCallback((msg: InboundEvent) => {
    if (msg.type === 'pong') { handlePong(); return; }
    if (msg.type === 'server_ping') return;
    if (msg.type === 'accepted') {
      const clientMsgId = typeof msg.data?.client_msg_id === 'string'
        ? msg.data.client_msg_id
        : undefined;
      if (clientMsgId) {
        outbox.current.delete(clientMsgId);
        removeOutboxPersistence(clientMsgId);
      }
      return;
    }
    if (msg.type === 'server_shutting_down') {
      stopHeartbeat();
      ws.current?.close();
      return;
    }
    if (msg.type === 'token_refreshed') return;
    if (msg.type === 'token_refresh_failed') return;
    if (msg.type === 'resume_gap') {
      const gapCurrentSeq = typeof msg.data?.current_seq === 'number'
        ? msg.data.current_seq as number
        : 0;

      if (gapCurrentSeq > 0) {
        lastAppliedSeq.current = gapCurrentSeq;
        lastSeq.current = Math.max(lastSeq.current, gapCurrentSeq);

        for (const s of [...pendingBySeq.current.keys()]) {
          if (s <= gapCurrentSeq) pendingBySeq.current.delete(s);
        }

        while (true) {
          const nextSeq = lastAppliedSeq.current + 1;
          const nextMsg = pendingBySeq.current.get(nextSeq);
          if (!nextMsg) break;
          pendingBySeq.current.delete(nextSeq);
          processOrderedMessage(nextMsg);
        }
      }

      const dmStore = useDMStore.getState();
      const convStore = useDMConversationStore.getState();
      void dmStore.loadUnreadCounts();
      void convStore.loadConversations();
      if (dmStore.activeAgent) {
        void dmStore.loadMessages(dmStore.activeAgent, false);
      }
      if (convStore.activeConversation) {
        void convStore.loadMessages(convStore.activeConversation);
      }
      return;
    }

    if (msg.type === 'token_expiring_soon') {
      ensureFreshToken().then((newToken) => {
        if (newToken && ws.current?.readyState === WebSocket.OPEN) {
          authToken.current = newToken;
          ws.current.send(JSON.stringify({
            type: 'token_refresh',
            data: { token: newToken },
          }));
        }
      });
      return;
    }

    if (msg.type === 'auth_expired') {
      stopHeartbeat();
      ws.current?.close();
      ensureFreshToken().then(() => scheduleReconnect());
      return;
    }

    handleServerEvent(msg as { type: string; data: Record<string, unknown> });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlePong, removeOutboxPersistence, stopHeartbeat]);

  const processOrderedMessage = useCallback((msg: InboundEvent) => {
    if (typeof msg.seq === 'number') {
      sendAck(msg.seq);
      lastAppliedSeq.current = msg.seq;
      if (isNewSeq(msg.seq)) {
        processServerEvent(msg);
      }
    } else {
      processServerEvent(msg);
    }
  }, [isNewSeq, processServerEvent, sendAck]);

  const processAuthenticatedMessage = useCallback((msg: InboundEvent) => {
    if (typeof msg.seq !== 'number') {
      processServerEvent(msg);
      return;
    }

    const seq = msg.seq;
    lastSeq.current = Math.max(lastSeq.current, seq);
    if (seq <= lastAppliedSeq.current) {
      sendAck(seq);
      return;
    }

    pendingBySeq.current.set(seq, msg);
    if (pendingBySeq.current.size > MAX_PENDING_BY_SEQ) {
      while (pendingBySeq.current.size > MAX_PENDING_BY_SEQ) {
        let minSeq: number | null = null;
        for (const s of pendingBySeq.current.keys()) {
          if (minSeq === null || s < minSeq) minSeq = s;
        }
        if (minSeq === null) break;
        pendingBySeq.current.delete(minSeq);
      }
    }
    if (seq > lastAppliedSeq.current + 1) {
      const now = Date.now();
      if (now - lastResumeRequestAt.current > 2000 && ws.current?.readyState === WebSocket.OPEN) {
        lastResumeRequestAt.current = now;
        ws.current.send(JSON.stringify({
          type: 'resume',
          data: { last_seq: lastAppliedSeq.current },
        }));
      }
    }
    while (true) {
      const nextSeq = lastAppliedSeq.current + 1;
      const nextMsg = pendingBySeq.current.get(nextSeq);
      if (!nextMsg) break;
      pendingBySeq.current.delete(nextSeq);
      processOrderedMessage(nextMsg);
    }
  }, [processOrderedMessage, processServerEvent, sendAck]);

  const flushPreAuthQueue = useCallback(() => {
    if (!isAuthenticated.current || preAuthQueue.current.length === 0) return;
    const queued = preAuthQueue.current;
    preAuthQueue.current = [];
    for (const msg of queued) {
      processAuthenticatedMessage(msg);
    }
  }, [processAuthenticatedMessage]);

  // ---- 연결 ----
  const connect = useCallback(async () => {
    if (isUnmounting.current) return;
    if (!navigator.onLine) return;

    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      connectionHealth.markDisconnected('dm');
      setWsConnected(false);
      return;
    }

    const token = await ensureFreshToken();
    if (!token) {
      scheduleReconnect();
      return;
    }
    authToken.current = token;

    const url = getDMWebSocketUrl();

    try {
      ws.current = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }

    ws.current.onopen = () => {
      isAuthenticated.current = false;
      preAuthQueue.current = [];
      ws.current?.send(JSON.stringify({
        type: 'auth',
        data: { token: authToken.current },
      }));
      authTimer.current = setTimeout(() => {
        ws.current?.close();
      }, AUTH_TIMEOUT_MS);
    };

    ws.current.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as InboundEvent;
        connectionHealth.recordMessageReceived('dm');
        if (msg.type === 'auth_ok') {
          const serverSeq = typeof msg.data?.current_seq === 'number'
            ? msg.data.current_seq as number
            : undefined;
          finishAuthHandshake(serverSeq);
          flushPreAuthQueue();
          return;
        }
        if (!isAuthenticated.current) {
          if (preAuthQueue.current.length >= MAX_PENDING_BY_SEQ) {
            preAuthQueue.current.shift();
          }
          preAuthQueue.current.push(msg);
          return;
        }

        processAuthenticatedMessage(msg);
      } catch {
        // ignore invalid JSON
      }
    };

    ws.current.onclose = () => {
      if (authTimer.current) {
        clearTimeout(authTimer.current);
        authTimer.current = undefined;
      }
      if (ackFlushTimer.current) {
        clearTimeout(ackFlushTimer.current);
        ackFlushTimer.current = undefined;
      }
      ackQueue.current.clear();
      isAuthenticated.current = false;
      preAuthQueue.current = [];
      stopHeartbeat();
      setWsConnected(false);
      connectionHealth.markReconnecting('dm');
      if (!isUnmounting.current) {
        scheduleReconnect();
      }
    };

    ws.current.onerror = () => {
      ws.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- 재연결 (jitter exponential backoff) ----
  const scheduleReconnect = useCallback(() => {
    if (isUnmounting.current) return;
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      connectionHealth.markDisconnected('dm');
      return;
    }

    // Jitter: delay * (0.5 + random * 0.5)
    const jitteredDelay = reconnectDelay.current * (0.5 + Math.random() * 0.5);
    reconnectAttempts.current++;

    reconnectTimer.current = setTimeout(() => {
      connect();
    }, jitteredDelay);

    // Flapping 방지: 30초 이상 유지됐으면 리셋
    if (connectedAt.current > 0 && Date.now() - connectedAt.current >= STABLE_CONNECTION_MS) {
      reconnectDelay.current = INITIAL_RECONNECT_DELAY;
    } else {
      reconnectDelay.current = Math.min(
        reconnectDelay.current * 2,
        MAX_RECONNECT_DELAY,
      );
    }
  }, [connect]);

  // ---- 서버 이벤트 처리 ----
  const handleServerEvent = useCallback(
    (event: { type: string; data: Record<string, unknown> }) => {
      const convStore = useDMConversationStore.getState();

      switch (event.type) {
        case 'dm_message': {
          const data = event.data as unknown as DMMessage;
          addMessage(data.agent_type as DMAgentType, data);
          if (data.agent_type === 'system_app') {
            void useTeamStore.getState().loadMyTeams();
            void convStore.loadConversations({ force: true });
          }
          break;
        }
        case 'typing_start': {
          const agentType = event.data.agent_type as DMAgentType;
          setTyping(agentType);
          break;
        }
        case 'typing_stop': {
          const agentType = event.data.agent_type as DMAgentType;
          clearTyping(agentType);
          break;
        }
        case 'read_receipt':
        case 'agent_status':
          break;

        case 'user_message': {
          const convId = event.data.conversation_id as string;
          const msg = event.data.message as UserMessage;
          if (convId && msg) {
            const hasConv = convStore.conversations.some(
              (c) => c.conversation_id === convId,
            );
            if (!hasConv) {
              useDMConversationStore.setState((prev) => {
                const existing = prev.messages[convId] || [];
                if (existing.some((m) => m.message_id === msg.message_id)) return prev;
                return {
                  messages: {
                    ...prev.messages,
                    [convId]: [...existing, msg],
                  },
                };
              });
              void convStore.ensureConversation(convId);
            } else {
              convStore.addMessage(convId, msg);
            }
          }
          break;
        }
        case 'user_dm_typing': {
          const convId = event.data.conversation_id as string;
          const userId = event.data.user_id as string;
          const typing = event.data.typing as boolean;
          if (convId && userId) {
            convStore.setTyping(convId, userId, typing);
          }
          break;
        }
        case 'conversation_read': {
          const readConvId = event.data.conversation_id as string;
          const readUserId = event.data.user_id as string;
          const myUserId = useUserStore.getState().user?.userId
            || useUserStore.getState().user?.email || '';
          if (readConvId && readUserId === myUserId) {
            convStore.updateConversationMeta(readConvId, { unread_count: 0 } as Partial<import('@auw/workspace-utils/types/dm').Conversation>);
          }
          break;
        }
        case 'conversation_update': {
          const convId = event.data.conversation_id as string;
          if (convId) {
            convStore.updateConversationMeta(convId, event.data as Partial<import('@auw/workspace-utils/types/dm').Conversation>);
          }
          break;
        }
        case 'user_reaction': {
          const convId = event.data.conversation_id as string;
          const messageId = event.data.message_id as string;
          const reactions = event.data.reactions as import('@auw/workspace-utils/types/dm').Reaction[];
          if (convId && messageId && Array.isArray(reactions)) {
            convStore.setMessageReactions(convId, messageId, reactions);
          }
          break;
        }
      }
    },
    [addMessage, setTyping, clearTyping],
  );

  // ---- 클라이언트 → 서버 전송 ----
  const sendReply = useCallback(
    (agentType: DMAgentType, content: string) => {
      enqueueOutbound('user_reply', { agent_type: agentType, content }, true);
    },
    [enqueueOutbound],
  );

  const sendMarkRead = useCallback((messageIds: string[]) => {
    enqueueOutbound('mark_read', { message_ids: messageIds });
  }, [enqueueOutbound]);

  const sendUserMessage = useCallback(
    (conversationId: string, content: string, contentType = 'text') => {
      enqueueOutbound(
        'send_user_message',
        { conversation_id: conversationId, content, content_type: contentType },
        true,
      );
    },
    [enqueueOutbound],
  );

  const sendConversationMarkRead = useCallback((conversationId: string) => {
    enqueueOutbound('mark_conversation_read', { conversation_id: conversationId });
  }, [enqueueOutbound]);

  const sendUserDMTyping = useCallback(
    (conversationId: string, typing: boolean) => {
      enqueueOutbound('user_dm_typing', { conversation_id: conversationId, typing });
    },
    [enqueueOutbound],
  );

  // ---- 라이프사이클 ----
  useEffect(() => {
    if (!enabled) {
      setWsConnected(false);
      return;
    }

    isUnmounting.current = false;
    void hydrateOutbox().finally(() => {
      if (isAuthenticated.current) {
        flushOutbox(true);
      }
      connect();
    });

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      if (!ws.current || ws.current.readyState === WebSocket.CLOSED || ws.current.readyState === WebSocket.CLOSING) {
        reconnectAttempts.current = 0;
        connect();
        return;
      }

      if (ws.current.readyState === WebSocket.OPEN) {
        if (!isAuthenticated.current) return;
        lastPingAt.current = Date.now();
        ws.current.send(JSON.stringify({ type: 'ping' }));
        if (pongTimer.current) clearTimeout(pongTimer.current);
        pongTimer.current = setTimeout(() => {
          ws.current?.close();
        }, PROBE_PONG_TIMEOUT);

        useDMStore.getState().loadUnreadCounts();
        useDMConversationStore.getState().loadConversations();
        useDMConversationStore.getState().flushPendingReads();
      }
    };

    const handleOnline = () => {
      if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
        reconnectAttempts.current = 0;
        reconnectDelay.current = INITIAL_RECONNECT_DELAY;
        connect();
      }
    };

    const handleOffline = () => {
      if (authTimer.current) {
        clearTimeout(authTimer.current);
        authTimer.current = undefined;
      }
      isAuthenticated.current = false;
      stopHeartbeat();
      preAuthQueue.current = [];
      if (ackFlushTimer.current) {
        clearTimeout(ackFlushTimer.current);
        ackFlushTimer.current = undefined;
      }
      ackQueue.current.clear();
      if (ws.current) {
        ws.current.onclose = null;
        ws.current.close();
        ws.current = null;
      }
      setWsConnected(false);
      connectionHealth.markDisconnected('dm');
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    outboxTimer.current = setInterval(() => {
      flushOutbox();
    }, Math.max(500, Math.floor(OUTBOX_RETRY_BASE_MS / 2)));

    return () => {
      isUnmounting.current = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (outboxTimer.current) {
        clearInterval(outboxTimer.current);
        outboxTimer.current = undefined;
      }
      stopHeartbeat();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (ws.current) {
        ws.current.onclose = null;
        ws.current.close();
      }
      if (authTimer.current) {
        clearTimeout(authTimer.current);
        authTimer.current = undefined;
      }
      if (ackFlushTimer.current) {
        clearTimeout(ackFlushTimer.current);
        ackFlushTimer.current = undefined;
      }
      ackQueue.current.clear();
      isAuthenticated.current = false;
      preAuthQueue.current = [];
      setWsConnected(false);
    };
  }, [enabled, connect, flushOutbox, hydrateOutbox, setWsConnected, stopHeartbeat]);

  return {
    sendReply,
    sendMarkRead,
    sendUserMessage,
    sendConversationMarkRead,
    sendUserDMTyping,
  };
}
